import React from 'react'
import PropTypes from 'prop-types'
import emailParser from 'email-addresses'

import { ENTER, UP, DOWN, ESCAPE, BACKSPACE, COMMA, TAB } from 'constants/keys'

import { findFirst } from 'util/arrays'
import debug from 'util/debug'
import { stopEvent } from 'util/keys'
import { isValidEmail } from 'util/strings'

import Dropdown from './Dropdown'
import SearchBox from './SearchBox'

const KEYS_IGNORED_ON_UP = [UP, DOWN, ESCAPE, COMMA, TAB]

class InputWithDropdown extends React.Component {
  static propTypes = {
    /* assumes contacts are to be added by name, if it doesnt pass email validation */
    allowCreateNewContactByName: PropTypes.bool,

    // override for our bloody SUI override.
    bgColor: PropTypes.string, // eslint-disable-line react/no-unused-prop-types

    /** Called when a recipient is selected. */
    onAdd: PropTypes.func,

    /** Called when a recipient is removed. */
    onRemove: PropTypes.func, // eslint-disable-line react/no-unused-prop-types

    /** Called after a To recipient is added (automatically by onAdd, but
     * documenting it here) */
    afterToAdd: PropTypes.func, // eslint-disable-line react/no-unused-prop-types

    /** Called when the input is blurred, CAVEAT: if this fn unmounts this
     * component, it will stop the dropdown menu items' onClick handler, so may
     * need to be debounced/cancelable.
     * */
    onBlur: PropTypes.func,

    /** Called when the input is focused. Can be used to cancel a pending
     * onBlur. */
    onFocus: PropTypes.func,

    // The initial list of recipients
    recipients: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types

    /** Triggers a parse & save on blur (not just enter/tab). Useful when used
     * in conjunction with allowCreateNewContactByName */
    saveOnBlur: PropTypes.bool,

    /** Triggers parse & save on tab key */
    saveOnTab: PropTypes.bool,

    /* Used by the connected component */
    ticketId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types

    // type of dropdown.
    type: PropTypes.oneOf(['to', 'cc', 'bcc']),
  }

  static defaultProps = {
    afterToAdd: undefined,
    bgColor: 'white',
    allowCreateNewContactByName: false,
    onAdd: undefined,
    onBlur: undefined,
    onFocus: undefined,
    onRemove: undefined,
    saveOnBlur: false,
    saveOnTab: true,
    type: undefined,
  }

  componentDidMount() {
    if (this.input && !this.props.noAutoFocus && this.input.value === '')
      this.input.focus()
  }

  componentDidUpdate(prevProps) {
    const { defaultValue } = this.props
    if (defaultValue !== prevProps.defaultValue) {
      this.input.value = defaultValue || ''
    }
  }

  componentWillUnmount() {
    // We don't need to clear saveFromBlurTimeoutID here because it would
    // break the saveOnBlur functionality in places where this component is
    // unmounted before we had a chance to save recipient (e.g. To while
    // forwarding). It's short lived and doesn't hold any significant
    // references, so there should not be any memory leaks.
  }

  keyUpHandler = searchFunc => {
    return e => {
      if (KEYS_IGNORED_ON_UP.indexOf(e.keyCode) > -1) return

      // Need to handle Enter in KeyUp because KeyDown is swallowed by
      // various dropdowns
      if (e.keyCode === ENTER) {
        stopEvent(e)
        const value = (this.input.value || '').trim()
        if (value) {
          this.parseAndSave(value)
        }
        return
      }

      let searchType = this.props.type
      if (searchType === 'bcc') searchType = 'cc'
      searchFunc(e.target.value, searchType)
    }
  }

  parseAndSave = (value, { noRefocus } = {}) => {
    const {
      onAdd,
      onUpdate,
      allowCreateNewContactByName,
      isForwarding,
      type,
      recipients,
    } = this.props
    const recipient = this.props.filtered[0]
    const parsed = emailParser.parseAddressList(value)

    if (isValidEmail(value)) {
      if (parsed) {
        parsed.forEach(email => {
          onAdd(
            {
              email: email.address,
              name: email.name || email.address,
            },
            recipients
          )
        })
      }
    } else if (recipient) {
      if (type === 'to' && !isForwarding) {
        onUpdate([recipient])
      } else {
        onAdd(recipient, recipients)
      }
    } else if (value && allowCreateNewContactByName) {
      onAdd({ name: value }, recipients)
    }
    this.clearInput({ noRefocus })
  }

  removeRecipient = ({ recipients, onRemove }) => {
    if (!recipients || !recipients.length || !onRemove) return true
    if (recipients.length > 0 && this.input.value === '') {
      return onRemove(recipients[recipients.length - 1], recipients)
    }
    return true
  }

  keyDownHandler = () => {
    return e => {
      const {
        allowMultipleRecipients,
        doClearUserSuggestion,
        filtered,
        saveOnTab,
      } = this.props
      const value = (this.input.value || '').trim()

      if (e.keyCode === ESCAPE) {
        stopEvent(e)
        this.clearInput()
        doClearUserSuggestion()
        return false
      }

      if (allowMultipleRecipients && e.keyCode === BACKSPACE) {
        this.removeRecipient(this.props)
      }

      if (e.keyCode === COMMA) {
        if (e.shiftKey) return true
        stopEvent(e)
        return this.parseAndSave(value)
      }

      if (e.keyCode === TAB && saveOnTab) {
        if (filtered && filtered.length !== 0) stopEvent(e)
        return this.parseAndSave(value)
      }

      return true
    }
  }

  pasteHandler = () => {
    const { allowMultipleRecipients, onAdd, sui, recipients } = this.props
    if (sui && !allowMultipleRecipients) return
    setTimeout(() => {
      const contents = this.input.value
      const parsed = emailParser.parseAddressList(contents)
      if (parsed) {
        parsed.forEach(email => {
          onAdd(
            {
              email: email.address,
              name: email.name,
            },
            recipients
          )
        })
        this.input.value = ''
      }
    }, 10)
  }

  handleSearchBoxBlur = e => {
    const { editing } = this.props

    // If the dropdown is visible, and a user clicks the scrollbar in the
    // DD (thus blurring the search input), we dont want to handle this event.
    if (editing) return false

    return this.handleDDBlur(e)
  }

  // When the search box/DD blurs, we want to do one or two things.
  //
  // 1. If the user clicks away, we assume this input has lost focus completely,
  // so we want the RecipientEditor to show the list of recipients again.
  //
  //    a. If saveOnBlur is set, then we want to try to validate the users'
  //       input as a valid email (i..e parseAndSave), adding that to the list.
  //
  // 2. If the user clicks away, but that click is to pick something from the
  // current recipient Dropdown, we do NOT want to handle this event. We let
  // handleRecipientClick do that for us.
  //
  handleDDBlur = e => {
    const { saveOnBlur, onBlur } = this.props

    if (!onBlur) return false

    if (saveOnBlur && this.input && this.input.value !== '') {
      const value = (this.input.value || '').trim()
      // we delay the parseAndSave in case the DD item selection triggers
      // continueEditing in the meantime
      this.saveFromBlurTimeoutID = setTimeout(
        () => this.parseAndSave(value, { noRefocus: true }),
        200
      )
    }

    return onBlur(e)
  }

  handleSearchBoxFocus = e => {
    const { onFocus } = this.props
    if (onFocus) onFocus(e)
    if (this.saveFromBlurTimeoutID) clearTimeout(this.saveFromBlurTimeoutID)
  }

  saveInputRef = node => {
    if (!node) return
    const { sui, allowMultipleRecipients, forwardRef } = this.props
    this.input =
      sui && !allowMultipleRecipients
        ? node.inputRef.current // WARNING (Serg): this is using SUI internals and might break on SUI update
        : node
    if (forwardRef) forwardRef(this.input)
  }

  handleRecipientClick = (recipientId, evt = undefined) => {
    const {
      onAdd,
      onUpdate,
      isForwarding,
      filtered,
      type,
      recipients,
    } = this.props
    const recipient = findFirst(filtered, r => r.id === recipientId)
    debug('recipient dropdown option clicked', { recipientId, recipient, evt })

    if (evt) stopEvent(evt)
    if (type === 'to' && !isForwarding) {
      onUpdate([recipient])
    } else {
      onAdd(recipient, recipients)
    }
    this.clearInput()
  }

  clearInput = ({ noRefocus } = {}) => {
    if (this.input) {
      if (!this.props.noAutoClear) this.input.value = ''
      // note! This will call continueEditing
      if (!noRefocus) this.input.focus()
    }
  }

  render() {
    const {
      allowMultipleRecipients,
      className,
      editing,
      error,
      filtered,
      isSearching,
      lastFilteredSize,
      onChange,
      placeholder = 'Search...',
      recipients,
      searchRecipients,
      defaultValue,
      sui,
      upward,
    } = this.props
    return (
      <Dropdown
        disabled={!editing}
        isSearching={isSearching}
        lastFilteredSize={lastFilteredSize}
        onClick={this.handleRecipientClick}
        onBlur={this.handleDDBlur}
        recipients={filtered}
        sui={sui}
        trigger={
          <SearchBox
            className={className}
            defaultValue={defaultValue}
            error={error}
            forwardRef={this.saveInputRef}
            multiple={allowMultipleRecipients}
            onChange={onChange}
            onKeyDown={this.keyDownHandler(searchRecipients)}
            onKeyUp={this.keyUpHandler(searchRecipients)}
            onPaste={this.pasteHandler}
            onFocus={this.handleSearchBoxFocus}
            onBlur={this.handleSearchBoxBlur}
            placeholder={
              sui ? placeholder : recipients.length === 0 && placeholder
            }
            sui={sui}
          />
        }
        upward={upward}
      />
    )
  }
}

export default InputWithDropdown
